/**
 * MicLevelVisualizer.js
 * Talkroute
 *
 * Created by Vel Genov on 2/6/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React, { createRef, useEffect, useRef, useState } from "react";

import { DEFAULT_AUDIO_SOURCE } from "../../actions/preferencesActions";
import { MicLevelVisualizerBarColors } from "./constants";

const activeColor = MicLevelVisualizerBarColors.active;
const inactiveColor = MicLevelVisualizerBarColors.inactive;

interface IMicLevelVisualizerProps {
  defaultAudioInput: string;
  defaultAudioInputVolume: number;
  visualizerSections?: number;
  isEnabled: boolean;
}

let audioContext: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let microphone: MediaStreamAudioSourceNode | null = null;
let scriptProcessor: any = null; //'ScriptProcessorNode' is deprecated.ts(6385)
let stream: MediaStream | null | undefined;

/**
 * Uses the defaultAudioInput param to get a sound stream and visualizes it
 */
const MicLevelVisualizer: React.FC<IMicLevelVisualizerProps> = ({
  defaultAudioInput,
  defaultAudioInputVolume,
  visualizerSections = 25,
  isEnabled,
}) => {
  const pidRef = useRef([]);

  if (pidRef.current.length !== visualizerSections) {
    // add or remove refs
    pidRef.current = Array(visualizerSections)
      .fill(0)
      .map((_, i) => pidRef.current[i] || createRef());
  }

  const [componentLoaded, setComponentLoaded] = useState(false);

  /**
   *  Get an audio stream for the defaultAudioInput
   */
  const getAudio = () => {
    const constraints = {
      audio: { deviceId: defaultAudioInput },
    };

    navigator.mediaDevices
      .getUserMedia(constraints)
      .then(gotAudioStream)
      .catch(handleError);
  };

  const handleError = (error) => {
    console.error(error);
  };

  /**
   * Called with an audio stream to process ot
   *
   * Doc on where some of the numbers and processing comes from
   * https://developer.mozilla.org/en-US/docs/Web/API/Web_Audio_API/Visualizations_with_Web_Audio_API
   *
   * @param stream an audio stream to process
   */
  const gotAudioStream = (streamProp: MediaStream) => {
    stream = streamProp;
    audioContext = new AudioContext();
    analyser = audioContext.createAnalyser();
    //  As a consequence of calling createMediaStreamSource(),
    // Audio playback from the media stream will be re-routed into the processing graph of the AudioContext
    microphone = audioContext.createMediaStreamSource(stream);
    scriptProcessor = audioContext.createScriptProcessor(2048, 1, 1);

    analyser.smoothingTimeConstant = 0.8;
    analyser.fftSize = 1024;

    microphone.connect(analyser);
    analyser.connect(scriptProcessor);
    scriptProcessor.connect(audioContext.destination);

    // const audioProcessor = () => {
    scriptProcessor.onaudioprocess = () => {
      if (!analyser) {
        return;
      }
      const frequencyArray = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(frequencyArray);

      // Calculate the average sound amplitude 0..100
      const average =
        frequencyArray.reduce(
          (accumulator, currentValue) => accumulator + currentValue
        ) / frequencyArray.length;

      // Update the rendering
      updatePids(average * defaultAudioInputVolume);
    };

    // scriptProcessor.addEventListener('audioprocess', audioProcessor);

    // Cleanup Function
    // return () => scriptProcessor.removeEventListener('audioprocess', audioProcessor)
  };

  /**
   * Disconnect all processors connected to the audio stream
   */
  const disconnectAudioStream = () => {
    if (audioContext) {
      audioContext.close();
      audioContext = null;
    }

    if (microphone) {
      microphone.disconnect();
      microphone = null;
    }

    if (analyser) {
      analyser.disconnect();
      analyser = null;
    }

    if (scriptProcessor) {
      scriptProcessor.disconnect();
      scriptProcessor = null;
    }

    if (stream) {
      stream.getTracks().forEach((track) => {
        track.stop();
      });
    }
  };

  /**
   * Update the pids with the passed in amplitude
   *
   * @param vol sound amplitude to display 0..100
   */
  const updatePids = (vol: number) => {
    const numActivePids = Math.round(vol / (100 / visualizerSections));
    for (const [index, element] of pidRef.current.entries()) {
      if (!(element && (element as any).current)) break;
      const { style } = (element as any).current;
      const newColor = index < numActivePids ? activeColor : inactiveColor;
      style.backgroundColor = newColor;
    }
  };

  /**
   * Create a style sheet based on the number of pid sections to display
   *
   * @returns a style to use with each pid
   */
  const createPidStyle = () => {
    const margin = 2;
    // Width for each element in %
    // Fractions tend to create problems
    const width = Math.floor(100 / visualizerSections);

    const pidStyle = {
      width: `calc(${width}% - ${margin * 2}px)`,
      height: "20px",
      display: "inline-block",
      margin: `${margin}px`,
      borderRadius: "2px",
      backgroundColor: inactiveColor,
    };

    return pidStyle;
  };

  useEffect(() => {
    setComponentLoaded(true);
    return () => disconnectAudioStream();
  }, []);

  const stopMicTracking = () => {
    // Disconnect and restart the microphone
    disconnectAudioStream();
    // Clear the PIDs
    updatePids(0);
  };

  useEffect(() => {
    if (componentLoaded) {
      if (isEnabled) {
        stopMicTracking();
        getAudio();
      }
    }
  }, [defaultAudioInput]);

  useEffect(() => {
    if (componentLoaded) {
      if (isEnabled) getAudio();
      else stopMicTracking();
    }
  }, [isEnabled, componentLoaded]);

  const drawMicVisualization = () => {
    const pidStyle = createPidStyle();

    // Create an array of size [0..visualizerSections] with elements from 0 to visualizerSection
    // Map over each element and create a div with reference equal to the elemenet value
    // The result looks like so:
    // <div ref='0'/>
    // <div ref='1'/>
    const pids = [...Array(visualizerSections).keys()].map((element) => (
      <div
        key={element}
        ref={pidRef.current[element]}
        style={{ ...pidStyle }}
      />
    ));

    return pids;
  };

  return <div style={{ width: "300px" }}>{drawMicVisualization()}</div>;
};

export default MicLevelVisualizer;
